import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row, Tab, Tabs } from 'react-bootstrap';
import { MdLockOutline } from "react-icons/md";
import { connect } from 'react-redux';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { fullName, handleInvalidForm, isBusinessAdmin, isManager } from '../../../../../services';
import { businessActions, businessCurrentTeamSelector, businessSelector, businessTeamsSelector, teamMembersSelector, unassignedTeamMembersSelector } from '../../../../../state/modules/business';
import { currentUserSelector } from '../../../../../state/modules/user';
import { noCapMap, pluralise } from '../../../../../utils';
import { ControlGroup, ControlOption, Icon, InputFormField } from '../../../../components';

const SubLabelText = (teamMembers, selectedTeamMembers, filteredTeamMembers) => {
  const hidden = teamMembers.length - filteredTeamMembers.length;
  if (selectedTeamMembers.length === 0 && hidden === 0) {
    return `${teamMembers.length} available ${pluralise('member', teamMembers.length)}`;
  } else if (selectedTeamMembers.length > 0 && hidden === 0) {
    return `${selectedTeamMembers.length} of ${teamMembers.length} ${pluralise('member', teamMembers.length)} selected`;
  } else if (selectedTeamMembers.length === 0 && hidden > 0) {
    return `${hidden} of ${teamMembers.length} ${pluralise('member', teamMembers.length)} hidden`;
  } else {
    return `${selectedTeamMembers.length} of ${teamMembers.length} ${pluralise('member', teamMembers.length)} selected, ${hidden} hidden`;
  }
}

const TeamEditCreate = ({
  setSearchQuery,
  filteredTeamMembers,
  teamMembers,
  searchQuery,
  selectedTeamMembers,
  toggleTeamMember,
  onSubmit,
  setFormRef,
  isBusinessAdmin,
  teamName,
  teamMemberLabel,
  setTeamName,
  teamNameValidationMessage,
  teamSelectionValidationMessage,
  currentBusinessTeam,
  isTeamManager }) => (
    <Grid>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <h1>
            {currentBusinessTeam ? 'Update Team' : 'Create your team'}
          </h1>
        </Col>
        <Col xs={12}>
          <form noValidate
            className={classNames('existingMembers', isTeamManager ? 'addingMembers' : '')}
            onSubmit={onSubmit}
            ref={(form) => setFormRef(form)}>
            <Row className='sm-gutter padded'>
              <Col xs={12} md={6}>
                {!isTeamManager ?
                  <InputFormField {...{
                    id: 'teamName',
                    label: currentBusinessTeam ? undefined : 'Name new team',
                    required: true,
                    value: teamName,
                    validation: teamNameValidationMessage,
                    onChange: (e) => setTeamName(e.target.value)
                  }} /> : null
                }
              </Col>
              <Col xs={12} mdOffset={2} md={4}>
                <InputFormField
                  id='search-desktop'
                  label='Search users'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  type='text-item search-item'>
                  <Icon icon='search-large' />
                </InputFormField>
              </Col>
            </Row>
            <Row className='padded'>
              <Col xs={12} md={6}>
                <Tabs id='addTeam'>
                  <Tab eventKey='SetUsers'
                    title='Team members'>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
            <fieldset>
              <Row>
                <Col xs={12}>
                  <ControlGroup
                    id='members'
                    label={null}
                    control='checkbox'
                    type='styled'
                    validation={teamSelectionValidationMessage}
                    onChange={(e) => {
                      const userId = parseInt(e.target.value, 10)
                      if (userId) {
                        toggleTeamMember(userId)
                      }
                    }}
                    colProps={{ xs: 12 }}>
                    {
                      noCapMap(({ user: { id, firstName, lastName }, teamIDs, teamNames, isTeamManager }, index) => {
                        const isUserNotClickable = isTeamManager && !isBusinessAdmin
                        return <ControlOption
                          key={id}
                          value={isUserNotClickable ? '' : id}
                          labelClassName={`control-option-${index % 2 ? 'odd' : 'even'} ${isUserNotClickable ? 'unclickable' : ''}`}
                          label={<span>{fullName({ firstName, lastName })}{teamMemberLabel(id, teamIDs, teamNames)}{isUserNotClickable ? <MdLockOutline /> : null}</span>}
                          checked={fp.contains(id)(selectedTeamMembers)}
                        />
                      }
                      )(fp.sortBy(({
                        user
                      }) => fp.lowerCase(fullName(user)))(filteredTeamMembers))
                    }
                  </ControlGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <label className={classNames('status-label', 'sub-label')}> {SubLabelText(teamMembers, selectedTeamMembers, filteredTeamMembers)}</label>
                </Col>
                <Col xs={10} xsOffset={1} md={6} mdOffset={3}>
                  <Button type='submit' className='large filled'>
                    {currentBusinessTeam ?
                      'Update team' : 'Create team'
                    }
                  </Button>
                </Col>
              </Row>
            </fieldset>
          </form>
        </Col>
      </Row>
    </Grid >
  )

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  teamMembers: teamMembersSelector(state),
  unassignedTeamMembers: unassignedTeamMembersSelector(state),
  businessTeamStats: businessTeamsSelector(state),
  team: businessCurrentTeamSelector(state)
});

const mapDispatchToProps = (dispatch) => ({
  createTeam: bindActionCreators(businessActions.business.team.createTeam, dispatch),
  setTeamMembers: bindActionCreators(businessActions.business.team.setTeamMembers, dispatch),
  renameTeam: bindActionCreators(businessActions.business.team.renameTeam, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ currentBusinessTeam, existingTeamMembers }) => ({
    selectedTeamMembers: fp.map(({ user }) => user.id)(existingTeamMembers),
    teamName: currentBusinessTeam ? he.decode(currentBusinessTeam.name) : '',
    searchQuery: ''
  }), {
      toggleTeamMember: ({ selectedTeamMembers }) => (id) => ({
        selectedTeamMembers: fp.includes(id)(selectedTeamMembers) ?
          fp.remove(fp.eq(id))(selectedTeamMembers) :
          fp.concat(id)(selectedTeamMembers)
      }),
      setTeamName: ({ teamName }) => (newTeamName) => ({
        teamName: newTeamName
      }),
      setSearchQuery: () => (newSearchQuery) => ({
        searchQuery: newSearchQuery
      })
    }
  ),
  withStateHandlers(() => ({
    formRef: null,
    teamNameValidationMessage: undefined,
    teamSelectionValidationMessage: undefined,
  }), {
      setFormRef: () => (ref) => ({
        formRef: ref
      }),
      setTeamNameValidationMessage: () => (msg) => ({
        teamNameValidationMessage: msg
      }),
      setTeamSelectionValidationMessage: () => (msg) => ({
        teamSelectionValidationMessage: msg
      })
    }
  ),
  withHandlers({
    validate: ({ currentBusinessTeam, businessTeamStats, teamName, selectedTeamMembers, setTeamNameValidationMessage, setTeamSelectionValidationMessage }) => () => {
      const nameValidationMessage =
        currentBusinessTeam ? undefined :
          !teamName || teamName === '' ? 'Team name is required' :
            fp.find(t => fp.toLower(t.team.name) === fp.toLower(teamName))(fp.get('businessTeams')(businessTeamStats)) ? 'Team name already in use' : undefined;
      const teamValidationMessage =
        !selectedTeamMembers || selectedTeamMembers.length === 0 ? 'At least one member must be selected' : undefined;

      setTeamNameValidationMessage(nameValidationMessage);
      setTeamSelectionValidationMessage(teamValidationMessage);
      return !nameValidationMessage && !teamValidationMessage;
    }
  }),
  withProps(({
    user,
    teamMembers,
    unassignedTeamMembers,
    team
  }) => ({
    isBusinessAdmin: isBusinessAdmin(user),
    teamMembers: isBusinessAdmin(user) ? teamMembers : team.members
  })),
  withHandlers({
    onSubmit: ({
      formRef,
      business,
      createTeam,
      teamName,
      teamMembers,
      selectedTeamMembers,
      teamNameValid,
      teamSelectionValid,
      currentBusinessTeam,
      setTeamMembers,
      callback,
      validate,
      renameTeam
    }) =>
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (validate()) {
          if (currentBusinessTeam) {
            renameTeam(
              business.id,
              currentBusinessTeam.id,
              {
                ...currentBusinessTeam,
                name: teamName
              }
            );
            setTeamMembers(
              business.id,
              currentBusinessTeam.id,
              fp.filter(u => fp.contains(u.id)(selectedTeamMembers))(fp.map(u => u.user)(teamMembers))
            );
          } else {
            createTeam(
              business.id,
              teamName,
              fp.filter(u => fp.contains(u.id)(selectedTeamMembers))(fp.map(u => u.user)(teamMembers)),
              [],
              [],
              callback ? true : false
            );
          }
          if (callback) {
            callback();
          }
        } else {
          if (formRef) {
            handleInvalidForm(formRef);
          }
        }
      }
  }),
  withHandlers({
    teamMemberLabel: ({ businessTeamStats, selectedTeamMembers, currentBusinessTeam }) => (id, teamIDs, teamNames) => {
      const teamName = teamNames.join(',')
      const isUserInCurrentTeam = currentBusinessTeam ? fp.contains(currentBusinessTeam.id)(teamIDs) : false
      return !teamName ? null :
        fp.contains(id)(selectedTeamMembers) ?
          !isUserInCurrentTeam ?
            <div className='span-label'>  will be moved from the {he.decode(teamName)} team</div> :
            <div className='span-label'>  currently in the {he.decode(teamName)} team</div> :
          !isUserInCurrentTeam ?
            <div className='span-label'>  currently in the {he.decode(teamName)} team</div> :
            <div className='span-label'>  will be removed from the {he.decode(teamName)} team</div>
    }
  }),
  withProps(({
    teamMembers,
    searchQuery
  }) => ({
    filteredTeamMembers: fp.filter(
      t => {
        const searchQueryEmpty = searchQuery === ''
        const searchQueryMatch = fp.includes(fp.toLower(searchQuery))(fp.toLower(fullName(t.user)))
        const isTrainingPlanManager = isManager(t)
        return !isTrainingPlanManager && (searchQueryEmpty || searchQueryMatch)
      }
    )(teamMembers)
  }))
)(TeamEditCreate)



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/team-edit-create.js